import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Avatar, Snackbar, FormControl, InputLabel, Select, MenuItem,
  Grid, IconButton, InputAdornment, Alert, Tooltip, Divider,
  TablePagination, LinearProgress, Badge
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import ModernLayout from '../common/ModernLayout';
import ModernCard from '../common/ModernCard';
import ModernButton, { GradientButton, GlassButton } from '../common/ModernButton';
import { StatsCard } from '../common/ModernCard';
import axiosInstance from '../axiosConfig';
import moment from 'moment';
import 'moment-timezone';
import * as XLSX from 'xlsx';
import { useLanguage } from '../../contexts/LanguageContext';

const AccessLogs = () => {
  const { translate } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [startDate, setStartDate] = useState(moment().startOf('day'));
  const [endDate, setEndDate] = useState(moment().endOf('day'));
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [justification, setJustification] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [showAllEntries, setShowAllEntries] = useState(false);

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    employees: 0,
    visitors: 0,
    late: 0,
    onTime: 0
  });

  const formatTime = (time) => {
    if (!time) return 'N/A';
  
    try {
      const localTime = time.replace("Z", "");
      return moment(localTime).format('HH:mm');
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'N/A';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return moment(date).format('MMM DD, YYYY');
    } catch (error) {
      return 'N/A';
    }
  };

  const isLate = (entryTime, schedule) => {
    if (!entryTime || !schedule) return false;
    
    try {
      const entry = moment(entryTime);
      const scheduleTime = moment(schedule, 'HH:mm');
      return entry.isAfter(scheduleTime);
    } catch (error) {
      console.error('Error calculating late time:', error);
      return false;
    }
  };

  const getStatusColor = (log) => {
    if (log.user_type === 'visitor') return 'info';
    if (isLate(log.entry_time, log.schedule)) return 'error';
    return 'success';
  };

  const getStatusIcon = (log) => {
    if (log.user_type === 'visitor') return <PersonIcon />;
    if (isLate(log.entry_time, log.schedule)) return <ErrorIcon />;
    return <CheckCircleIcon />;
  };

  const getStatusText = (log) => {
    if (log.user_type === 'visitor') return 'Visitor';
    if (isLate(log.entry_time, log.schedule)) return 'Late';
    return 'On Time';
  };

  const calculateTotalHoursWorked = (log) => {
    if (!log.workstation_entry_time || !log.workday_end) return 'N/A';
    
    try {
      const entry = moment(log.workstation_entry_time);
      const exit = moment(log.workday_end);
      const duration = moment.duration(exit.diff(entry));
      const hours = duration.asHours();
      
      return hours.toFixed(2);
    } catch (error) {
      console.error('Error calculating total hours:', error);
      return 'N/A';
    }
  };

  const calculateBreakViolations = (log) => {
    if (log.user_type === 'visitor') return null;
    
    const violations = [];
    
    // Check Break 1 (15 min limit)
    if (log.break1_start && log.break1_end) {
      const break1Start = moment(log.break1_start);
      const break1End = moment(log.break1_end);
      const break1Duration = moment.duration(break1End.diff(break1Start));
      const break1Minutes = break1Duration.asMinutes();
      
      if (break1Minutes > 15) {
        const extraMinutes = break1Minutes - 15;
        violations.push(`B1: +${extraMinutes.toFixed(0)}min`);
      }
    }
    
    // Check Break 2 (Lunch - 60 min limit)
    if (log.break2_start && log.break2_end) {
      const break2Start = moment(log.break2_start);
      const break2End = moment(log.break2_end);
      const break2Duration = moment.duration(break2End.diff(break2Start));
      const break2Minutes = break2Duration.asMinutes();
      
      if (break2Minutes > 60) {
        const extraMinutes = break2Minutes - 60;
        violations.push(`B2: +${extraMinutes.toFixed(0)}min`);
      }
    }
    
    // Check Break 3 (15 min limit)
    if (log.break3_start && log.break3_end) {
      const break3Start = moment(log.break3_start);
      const break3End = moment(log.break3_end);
      const break3Duration = moment.duration(break3End.diff(break3Start));
      const break3Minutes = break3Duration.asMinutes();
      
      if (break3Minutes > 15) {
        const extraMinutes = break3Minutes - 15;
        violations.push(`B3: +${extraMinutes.toFixed(0)}min`);
      }
    }
    
    return violations.length > 0 ? violations.join(', ') : null;
  };

  const getBreakViolationColor = (violations) => {
    if (!violations) return 'default';
    
    const totalExtraMinutes = violations.split(', ').reduce((total, violation) => {
      const minutes = parseInt(violation.match(/\d+/)[0]);
      return total + minutes;
    }, 0);
    
    if (totalExtraMinutes > 30) return 'error';
    if (totalExtraMinutes > 15) return 'warning';
    return 'info';
  };

  const calculateEffectiveHoursWorked = (log) => {
    if (!log.workstation_entry_time || !log.workday_end) return 'N/A';
    
    try {
      const entry = moment(log.workstation_entry_time);
      const exit = moment(log.workday_end);
      let totalDuration = moment.duration(exit.diff(entry));
      
      // Subtract break violations
      const violations = calculateBreakViolations(log);
      if (violations) {
        const extraMinutes = violations.split(', ').reduce((total, violation) => {
          const minutes = parseInt(violation.match(/\d+/)[0]);
          return total + minutes;
        }, 0);
        
        totalDuration.subtract(extraMinutes, 'minutes');
      }
      
      const hours = totalDuration.asHours();
      return hours.toFixed(2);
    } catch (error) {
      console.error('Error calculating effective hours:', error);
      return 'N/A';
    }
  };

  const consolidateLogsByEmployee = (logs) => {
    const consolidated = {};
    
    logs.forEach(log => {
      const employeeId = log.user_id || log.employee_id;
      const date = moment(log.entry_time || log.timestamp).format('YYYY-MM-DD');
      const key = `${employeeId}_${date}`;
      
      if (!consolidated[key]) {
        // Initialize with basic info
        consolidated[key] = {
          id: log.id,
          user_id: employeeId,
          name: log.name || log.employee_name,
          email: log.email,
          user_type: log.user_type,
          photo_url: log.photo_url,
          schedule: log.schedule,
          entry_time: log.entry_time || log.timestamp,
          exit_time: log.exit_time,
          workstation_entry_time: log.workstation_entry_time,
          workday_end: log.workday_end,
          break1_start: log.break1_start,
          break1_end: log.break1_end,
          break2_start: log.break2_start,
          break2_end: log.break2_end,
          break3_start: log.break3_start,
          break3_end: log.break3_end,
          // Keep track of the most recent timestamp for sorting
          latest_timestamp: log.entry_time || log.timestamp
        };
      } else {
        // Merge additional data from other records
        const existing = consolidated[key];
        
        // Update timestamps if we have more complete data
        if (log.workstation_entry_time && !existing.workstation_entry_time) {
          existing.workstation_entry_time = log.workstation_entry_time;
        }
        if (log.workday_end && !existing.workday_end) {
          existing.workday_end = log.workday_end;
        }
        if (log.exit_time && !existing.exit_time) {
          existing.exit_time = log.exit_time;
        }
        
        // Merge break data
        if (log.break1_start && !existing.break1_start) {
          existing.break1_start = log.break1_start;
          existing.break1_end = log.break1_end;
        }
        if (log.break2_start && !existing.break2_start) {
          existing.break2_start = log.break2_start;
          existing.break2_end = log.break2_end;
        }
        if (log.break3_start && !existing.break3_start) {
          existing.break3_start = log.break3_start;
          existing.break3_end = log.break3_end;
        }
        
        // Update latest timestamp
        const currentTime = moment(log.entry_time || log.timestamp);
        const existingTime = moment(existing.latest_timestamp);
        if (currentTime.isAfter(existingTime)) {
          existing.latest_timestamp = log.entry_time || log.timestamp;
        }
      }
    });
    
    // Convert back to array and sort by latest timestamp
    return Object.values(consolidated).sort((a, b) => 
      moment(b.latest_timestamp).diff(moment(a.latest_timestamp))
    );
  };

  // Fetch logs when component mounts or dates change
  useEffect(() => {
    fetchAccessLogs();
  }, [startDate, endDate]);

  // Apply filters when search term or filters change
  useEffect(() => {
    applyFilters();
  }, [searchTerm, filterType, filterStatus, logs]);

  // Fetch logs when showAllEntries changes
  useEffect(() => {
    fetchAccessLogs();
  }, [showAllEntries]);

  const fetchAccessLogs = async () => {
    try {
      setLoading(true);
      const params = {
        startDate: startDate ? startDate.toISOString() : null,
        endDate: endDate ? endDate.toISOString() : null
      };
      
      // Only add limit if we don't want to show all entries
      if (!showAllEntries) {
        params.limit = 1000; // Increase default limit for better UX
      }
      
      const response = await axiosInstance.get('/api/access-logs', { params });
      
      // Consolidate duplicate entries by employee and date
      const consolidatedLogs = consolidateLogsByEmployee(response.data);
      
      setLogs(consolidatedLogs);
      calculateStats(consolidatedLogs);
    } catch (error) {
      console.error('Error fetching access logs:', error);
      showSnackbar('Error al cargar los registros de acceso', 'error');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (logData) => {
    const employeeLogs = logData.filter(log => log.user_type === 'employee');
    const totalEffectiveHours = employeeLogs.reduce((total, log) => {
      const hours = calculateEffectiveHoursWorked(log);
      return total + (hours !== 'N/A' ? parseFloat(hours) : 0);
    }, 0);
    
    const totalBreakViolations = employeeLogs.reduce((total, log) => {
      const violations = calculateBreakViolations(log);
      if (!violations) return total;
      
      const extraMinutes = violations.split(', ').reduce((sum, violation) => {
        const minutes = parseInt(violation.match(/\d+/)[0]);
        return sum + minutes;
      }, 0);
      
      return total + extraMinutes;
    }, 0);
    
    const stats = {
      total: logData.length,
      employees: logData.filter(log => log.user_type === 'employee').length,
      visitors: logData.filter(log => log.user_type === 'visitor').length,
      late: logData.filter(log => log.user_type === 'employee' && isLate(log.entry_time, log.schedule)).length,
      onTime: logData.filter(log => log.user_type === 'employee' && !isLate(log.entry_time, log.schedule)).length,
      effectiveHours: totalEffectiveHours.toFixed(1),
      breakViolations: totalBreakViolations
    };
    setStats(stats);
  };

  const applyFilters = () => {
    let filtered = [...logs];

    // Search by name
    if (searchTerm) {
      filtered = filtered.filter(log => 
        log.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by user type
    if (filterType !== 'all') {
      filtered = filtered.filter(log => log.user_type === filterType);
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(log => {
        if (filterStatus === 'late') {
          return log.user_type === 'employee' && isLate(log.entry_time, log.schedule);
        } else if (filterStatus === 'onTime') {
          return log.user_type === 'employee' && !isLate(log.entry_time, log.schedule);
        } else if (filterStatus === 'visitor') {
          return log.user_type === 'visitor';
        }
        return true;
      });
    }

    setFilteredLogs(filtered);
    setPage(0); // Reset to first page when filtering
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleFilterChange = (event) => {
    setFilterType(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setFilterStatus(event.target.value);
  };

  const handleStartDateChange = (date) => {
    setStartDate(moment(date));
  };

  const handleEndDateChange = (date) => {
    setEndDate(moment(date));
  };

  const handleOpenDialog = (log) => {
    setSelectedLog(log);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setJustification('');
  };

  const handleJustificationChange = (event) => {
    setJustification(event.target.value);
  };

  const handleSubmitJustification = async () => {
    if (!justification.trim()) {
      showSnackbar('Por favor ingrese una justificación', 'warning');
      return;
    }

    try {
      await axiosInstance.post('/api/justify-late-entry', {
        logId: selectedLog.id,
        justification
      });
      showSnackbar('Justificación enviada con éxito', 'success');
      fetchAccessLogs(); // Refresh logs
      handleCloseDialog();
    } catch (error) {
      console.error('Error submitting justification:', error);
      showSnackbar('Error enviando justificación', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleExportToExcel = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/export-access-logs', {
        responseType: 'blob',
        params: {
          startDate: startDate ? startDate.toISOString() : null,
          endDate: endDate ? endDate.toISOString() : null
        }
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `access_logs_${moment().format('YYYY-MM-DD')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar('Archivo exportado exitosamente', 'success');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      showSnackbar('Error al exportar registros', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleExportFilteredData = () => {
    try {
      setLoading(true);
      
      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      
      // Prepare data for export
      const exportData = filteredLogs.map(log => ({
        'Nombre': log.name || 'N/A',
        'Email': log.email || 'N/A',
        'Tipo': log.user_type === 'employee' ? 'Empleado' : 'Visitante',
        'Fecha': formatDate(log.entry_time),
        'Hora Entrada': formatTime(log.entry_time),
        'Hora Salida': formatTime(log.exit_time),
        'Hora Puesto': formatTime(log.workstation_entry_time),
        'Hora Fin Jornada': formatTime(log.workday_end),
        'Break 1': log.break1_start && log.break1_end ? 
          `${formatTime(log.break1_start)}-${formatTime(log.break1_end)}` : 'N/A',
        'Break 2': log.break2_start && log.break2_end ? 
          `${formatTime(log.break2_start)}-${formatTime(log.break2_end)}` : 'N/A',
        'Break 3': log.break3_start && log.break3_end ? 
          `${formatTime(log.break3_start)}-${formatTime(log.break3_end)}` : 'N/A',
        'Estado': getStatusText(log),
        'Horas Totales': calculateTotalHoursWorked(log) !== 'N/A' ? 
          `${calculateTotalHoursWorked(log)}h` : 'N/A',
        'Horas Efectivas': calculateEffectiveHoursWorked(log) !== 'N/A' ? 
          `${calculateEffectiveHoursWorked(log)}h` : 'N/A',
        'Advertencias': calculateBreakViolations(log) || 'N/A',
        'Horario Programado': log.schedule || 'N/A'
      }));

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const columnWidths = [
        { wch: 20 }, // Nombre
        { wch: 25 }, // Email
        { wch: 12 }, // Tipo
        { wch: 15 }, // Fecha
        { wch: 12 }, // Hora Entrada
        { wch: 12 }, // Hora Salida
        { wch: 12 }, // Hora Puesto
        { wch: 15 }, // Hora Fin Jornada
        { wch: 15 }, // Break 1
        { wch: 15 }, // Break 2
        { wch: 15 }, // Break 3
        { wch: 12 }, // Estado
        { wch: 12 }, // Horas Totales
        { wch: 12 }, // Horas Efectivas
        { wch: 20 }, // Advertencias
        { wch: 15 }  // Horario Programado
      ];
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Access Logs');

      // Create summary sheet
      const summaryData = [
        { 'Métrica': 'Total de Registros', 'Valor': stats.total },
        { 'Métrica': 'Empleados', 'Valor': stats.employees },
        { 'Métrica': 'Visitantes', 'Valor': stats.visitors },
        { 'Métrica': 'A Tiempo', 'Valor': stats.onTime },
        { 'Métrica': 'Retrasos', 'Valor': stats.late },
        { 'Métrica': 'Horas Efectivas Totales', 'Valor': `${stats.effectiveHours}h` },
        { 'Métrica': 'Violaciones de Break (min)', 'Valor': stats.breakViolations },
        { 'Métrica': 'Fecha de Exportación', 'Valor': moment().format('DD/MM/YYYY HH:mm') },
        { 'Métrica': 'Filtros Aplicados', 'Valor': getAppliedFiltersText() }
      ];

      const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
      summaryWorksheet['!cols'] = [{ wch: 30 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Resumen');

      // Generate filename based on filters
      const filename = generateExportFilename();

      // Save file
      XLSX.writeFile(workbook, filename);
      
      showSnackbar('Datos filtrados exportados exitosamente', 'success');
    } catch (error) {
      console.error('Error exporting filtered data:', error);
      showSnackbar('Error al exportar datos filtrados', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getAppliedFiltersText = () => {
    const filters = [];
    
    if (searchTerm) filters.push(`Búsqueda: "${searchTerm}"`);
    if (filterType !== 'all') filters.push(`Tipo: ${filterType === 'employee' ? 'Empleados' : 'Visitantes'}`);
    if (filterStatus !== 'all') {
      const statusText = {
        'onTime': 'A Tiempo',
        'late': 'Retrasos',
        'visitor': 'Visitantes'
      };
      filters.push(`Estado: ${statusText[filterStatus]}`);
    }
    if (startDate && endDate) {
      filters.push(`Período: ${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`);
    }
    
    return filters.length > 0 ? filters.join(', ') : 'Sin filtros';
  };

  const generateExportFilename = () => {
    const baseName = 'access_logs';
    const timestamp = moment().format('YYYY-MM-DD_HH-mm');
    
    let filename = `${baseName}_${timestamp}`;
    
    // Add filter info to filename
    if (searchTerm) {
      const cleanSearch = searchTerm.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 20);
      filename += `_${cleanSearch}`;
    }
    
    if (filterType !== 'all') {
      filename += `_${filterType}`;
    }
    
    if (startDate && endDate) {
      filename += `_${startDate.format('YYYY-MM-DD')}_to_${endDate.format('YYYY-MM-DD')}`;
    }
    
    return `${filename}.xlsx`;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterType('all');
    setFilterStatus('all');
    setStartDate(moment().startOf('day'));
    setEndDate(moment().endOf('day'));
  };

  const handleToggleShowAll = () => {
    setShowAllEntries(!showAllEntries);
  };

  const handleToggleShowAllAndFetch = async () => {
    setShowAllEntries(!showAllEntries);
    // Fetch will be triggered by useEffect when showAllEntries changes
  };

  const paginatedLogs = filteredLogs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <ModernLayout title={translate('accessLogs.title')}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {translate('accessLogs.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {translate('accessLogs.subtitle')}
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('accessLogs.totalEntries')}
            value={stats.total}
            icon={<PersonIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('accessLogs.employees')}
            value={stats.employees}
            icon={<PersonIcon />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('accessLogs.visitors')}
            value={stats.visitors}
            icon={<PersonIcon />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('accessLogs.onTime')}
            value={stats.onTime}
            icon={<CheckCircleIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('accessLogs.late')}
            value={stats.late}
            icon={<ErrorIcon />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('accessLogs.effectiveHours')}
            value={stats.effectiveHours}
            icon={<ScheduleIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('accessLogs.breakViolations')}
            value={stats.breakViolations}
            icon={<WarningIcon />}
            color="error"
          />
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <ModernCard
        title={translate('accessLogs.filtersAndSearch')}
        subtitle={translate('accessLogs.filtersAndSearchSubtitle')}
        sx={{ mb: 3 }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Search Bar */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder={translate('accessLogs.searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={clearSearch}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          {/* Quick Filters */}
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>{translate('accessLogs.type')}</InputLabel>
                <Select value={filterType} onChange={handleFilterChange}>
                  <MenuItem value="all">{translate('accessLogs.all')}</MenuItem>
                  <MenuItem value="employee">{translate('accessLogs.employees')}</MenuItem>
                  <MenuItem value="visitor">{translate('accessLogs.visitors')}</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>{translate('accessLogs.status')}</InputLabel>
                <Select value={filterStatus} onChange={handleStatusFilterChange}>
                  <MenuItem value="all">{translate('accessLogs.all')}</MenuItem>
                  <MenuItem value="onTime">{translate('accessLogs.onTime')}</MenuItem>
                  <MenuItem value="late">{translate('accessLogs.late')}</MenuItem>
                  <MenuItem value="visitor">{translate('accessLogs.visitors')}</MenuItem>
                </Select>
              </FormControl>

              <GlassButton
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? translate('accessLogs.hideFilters') : translate('accessLogs.moreFilters')}
              </GlassButton>

              <GlassButton
                variant="outlined"
                onClick={clearAllFilters}
                disabled={!searchTerm && filterType === 'all' && filterStatus === 'all'}
              >
                {translate('accessLogs.clear')}
              </GlassButton>
            </Box>
          </Grid>

          {/* Advanced Filters */}
          {showFilters && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {translate('accessLogs.advancedFilters')}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={translate('accessLogs.startDate')}
                    value={startDate.toDate()}
                    onChange={handleStartDateChange}
                    renderInput={(params) => <TextField {...params} size="small" />}
                  />
                  <DatePicker
                    label={translate('accessLogs.endDate')}
                    value={endDate.toDate()}
                    onChange={handleEndDateChange}
                    renderInput={(params) => <TextField {...params} size="small" />}
                  />
                </LocalizationProvider>
                <GradientButton onClick={fetchAccessLogs} disabled={loading}>
                  {translate('accessLogs.applyFilters')}
                </GradientButton>
              </Box>
            </Grid>
          )}
        </Grid>
      </ModernCard>

      {/* Results Info */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {getAppliedFiltersText()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={showAllEntries ? translate('accessLogs.showOnly1000') : translate('accessLogs.showAllEntries')}>
            <IconButton onClick={handleToggleShowAllAndFetch} disabled={loading}>
              {showAllEntries ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title={translate('accessLogs.refresh')}>
            <IconButton onClick={fetchAccessLogs} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={translate('accessLogs.exportFilteredExcel')}>
            <IconButton 
              onClick={handleExportFilteredData} 
              disabled={loading || filteredLogs.length === 0}
              color="primary"
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={translate('accessLogs.exportAllExcel')}>
            <IconButton onClick={handleExportToExcel} disabled={loading}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Loading Progress */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Table */}
      <ModernCard
        title={translate('accessLogs.accessLogsTitle')}
        subtitle={`${translate('accessLogs.showing')} ${paginatedLogs.length} ${translate('accessLogs.of')} ${filteredLogs.length} ${translate('accessLogs.records')}`}
        sx={{ mb: 3 }}
      >
        <TableContainer sx={{ 
          maxHeight: 600, 
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{translate('accessLogs.user')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('accessLogs.type')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('accessLogs.date')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('accessLogs.entryTime')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('accessLogs.exitTime')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('accessLogs.status')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('accessLogs.totalHoursWorked')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('accessLogs.breakViolations')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('accessLogs.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedLogs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar 
                        sx={{ width: 32, height: 32 }}
                        src={log.photo_url || log.profile_image || log.avatar_url}
                        alt={log.name || 'User'}
                      >
                        {log.name?.charAt(0) || 'U'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {log.name || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {log.email || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.user_type === 'visitor' ? 'Visitante' : 'Empleado'}
                      color={log.user_type === 'visitor' ? 'info' : 'primary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(log.entry_time)}</TableCell>
                  <TableCell>{formatTime(log.entry_time)}</TableCell>
                  <TableCell>{formatTime(log.workday_end)}</TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(log)}
                      label={getStatusText(log)}
                      color={getStatusColor(log)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {calculateTotalHoursWorked(log)}h
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {calculateBreakViolations(log) && (
                      <Chip
                        label={calculateBreakViolations(log)}
                        color={getBreakViolationColor(calculateBreakViolations(log))}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(log)}
                      disabled={log.user_type === 'visitor'}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={filteredLogs.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={translate('accessLogs.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${translate('accessLogs.of')} ${count}`}
        />
      </ModernCard>

      {/* Justification Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {translate('accessLogs.justifyLateEntry')} - {selectedLog?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label={translate('accessLogs.justification')}
              value={justification}
              onChange={handleJustificationChange}
              placeholder={translate('accessLogs.justificationPlaceholder')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={handleCloseDialog}>
            {translate('accessLogs.cancel')}
          </GlassButton>
          <GradientButton onClick={handleSubmitJustification}>
            {translate('accessLogs.submitJustification')}
          </GradientButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{
            borderRadius: 2,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </ModernLayout>
  );
};

export default AccessLogs;